import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import ComboBoxSearch from "@/components/ComboBoxSearch";
import useProductionUnit from "@/hooks/useProductionUnit";
import { Search, Plus, Save, X, Sparkles, ArrowLeft, CheckCircle2, Ban, Eye } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
const STATUS_TONE = {
  on_process: "bg-amber-50 text-amber-800 border-amber-200",
  partial: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

export default function Plating() {
  const [view, setView] = useState({ mode: "list" });        // {mode:'list'|'new'|'edit',id}
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [productionUnitId, setProductionUnitId] = useState("");
  const [workerOpts, setWorkerOpts] = useState([]);
  const [unitOpts, setUnitOpts] = useState([]);
  const [receiveFor, setReceiveFor] = useState(null);

  useEffect(() => {
    api.get("/production/workers").then((r) => setWorkerOpts(r.data || [])).catch(() => {});
    api.get("/production/units").then((r) => setUnitOpts((r.data || []).filter((u) => u.is_active !== false))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const params = { page: 1, page_size: 100 };
      if (status) params.status = status;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (workerId) params.worker_id = workerId;
      if (productionUnitId) params.production_unit_id = productionUnitId;
      const r = await api.get("/production/platings", { params });
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [status, search, dateFrom, dateTo, workerId, productionUnitId]);
  useEffect(() => { if (view.mode === "list") load(); }, [view.mode, load]);

  if (view.mode === "new" || view.mode === "edit") {
    return <PlatingEditor batchId={view.id} onBack={() => setView({ mode: "list" })} />;
  }

  return (
    <div className="space-y-4">
      <PageSection title="Level 2 — Plating" description="Issue RM → gold/rhodium plate → finished pieces (ready for QC)" action={
        <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setView({ mode: "new" })} data-testid="plt-new-btn">
          <Plus className="h-4 w-4" /> New L2 Batch
        </Button>
      }>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs min-w-[200px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search batch # / RM..." className="pl-8" data-testid="plt-search" />
            </div>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" title="From date" data-testid="plt-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" title="To date" data-testid="plt-date-to" />
            <select value={workerId} onChange={(e) => setWorkerId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="plt-worker-filter">
              <option value="">All workers</option>
              {workerOpts.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            {unitOpts.length > 1 && (
              <select value={productionUnitId} onChange={(e) => setProductionUnitId(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="plt-unit-filter">
                <option value="">All units</option>
                {unitOpts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="plt-status-filter">
              <option value="">All statuses</option>
              <option value="on_process">On Process</option>
              <option value="partial">Partial</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            {(dateFrom || dateTo || workerId || productionUnitId || status || search) && (
              <button onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setWorkerId(""); setProductionUnitId(""); setStatus(""); }} className="text-[11px] text-gray-500 hover:text-gray-900 underline">Clear</button>
            )}
            <span className="text-xs text-gray-500 ml-auto">{items.length} batches</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Batch #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">RMs</th>
                <th className="px-3 py-2 text-right">Issued</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Damaged</th>
                <th className="px-3 py-2 text-right">Lost</th>
                <th className="px-3 py-2 text-right">Pending</th>
                <th className="px-3 py-2">Unit</th>
                <th className="px-3 py-2">Worker</th>
                <th className="px-3 py-2 text-right">Plating Cost</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={13} className="px-3 py-10 text-center text-gray-400">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  No L2 batches yet.
                </td></tr>
              )}
              {items.map((b) => {
                const issuedTotal = (b.rm_lines || []).reduce((s, l) => s + Number(l.issued_qty || 0), 0);
                const rcvTotal = Number(b.received_qty || 0);
                const dmgTotal = Number(b.damaged_qty || 0);
                const lostTotal = Number(b.lost_qty || 0);
                const pending = Math.max(0, issuedTotal - rcvTotal - dmgTotal - lostTotal);
                const canReceive = ["on_process", "partial"].includes(b.status);
                return (
                  <tr key={b.id} className="hover:bg-gray-50" data-testid={`plt-row-${b.id}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{b.batch_no}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDate(b.production_date)}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs">{(b.rm_lines || []).slice(0, 2).map((l) => l.raw_material_name).join(", ")}{(b.rm_lines || []).length > 2 ? ` +${b.rm_lines.length - 2}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{issuedTotal || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-emerald-700">{rcvTotal || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-amber-700">{dmgTotal || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-red-700">{lostTotal || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold">{pending > 0 ? pending : "—"}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{b.production_unit_name || "—"}</td>
                    <td className="px-3 py-2 text-gray-700 text-xs">{b.worker_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{formatINR(b.plating_cost || 0)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[b.status] || STATUS_TONE.cancelled}`}>
                        {(b.status || "").replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canReceive && (
                        <>
                          <button onClick={() => setReceiveFor(b)} className="h-7 px-2 text-xs rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200 mr-1" data-testid={`plt-receive-${b.id}`}>
                            Receive
                          </button>
                          <button onClick={async () => {
                            if (!window.confirm(`Cancel batch ${b.batch_no}? RM will be returned to stock.`)) return;
                            try { await api.post(`/production/platings/${b.id}/cancel`); toast.success("Cancelled"); load(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
                          }} className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center" data-testid={`plt-cancel-${b.id}`} title="Cancel batch">
                            <Ban className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        </>
                      )}
                      <button onClick={() => setView({ mode: "edit", id: b.id })} className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center ml-1" data-testid={`plt-view-${b.id}`}>
                        <Eye className="h-3.5 w-3.5 text-gray-500" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Surface>
      </PageSection>

      {receiveFor && <PlatingReceiveDialog batch={receiveFor} onClose={() => setReceiveFor(null)} onDone={() => { setReceiveFor(null); load(); }} />}
    </div>
  );
}

// ------------------ Editor ------------------

function PlatingEditor({ batchId, onBack }) {
  const isNew = !batchId;
  const [batch, setBatch] = useState(null);
  const [workerOptions, setWorkerOptions] = useState([]);
  const [rmStockMap, setRmStockMap] = useState({});   // { rm_id: available_qty }
  const [rmOptions, setRmOptions] = useState([]);          // options for combobox — sourced from raw-materials with current_stock
  const { units: unitOptions, unitId, setUnitId, required: unitRequired } = useProductionUnit();
  const [form, setForm] = useState({
    production_date: new Date().toISOString().slice(0, 10),
    worker_id: null,
    notes: "",
    rm_lines: [{ raw_material_id: null, raw_material_name: "", unit: "", issued_qty: "" }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/production/workers").then((r) => setWorkerOptions((r.data || []).map((w) => ({ value: w.id, label: w.name, sublabel: w.worker_type || "" }))));
  }, []);

  // Server-side typeahead — never cap the pool on the client. Availability is
  // fetched lazily via /rm-availability once an RM is picked (or on edit
  // load), so the picker itself can list any RM in the catalog.
  const searchRMs = useCallback(async (q) => {
    try {
      const r = await api.get("/production/raw-materials", { params: { search: q || "", page: 1, page_size: 30 } });
      setRmOptions((r.data?.items || []).map((rm) => ({ value: rm.id, label: rm.name, sublabel: rm.unit || "" })));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { searchRMs(""); }, [searchRMs]);

  const loadRmStock = useCallback(async (ids) => {
    const missing = (ids || []).filter(Boolean);
    if (!missing.length) return;
    try {
      const r = await api.get("/production/rm-availability", { params: { ids: missing.join(",") } });
      setRmStockMap((prev) => ({ ...prev, ...(r.data || {}) }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (batchId) {
      api.get(`/production/platings/${batchId}`).then((r) => {
        setBatch(r.data);
        setForm({
          production_date: r.data.production_date,
          worker_id: r.data.worker_id,
          notes: r.data.notes || "",
          rm_lines: r.data.rm_lines || [],
        });
        if (r.data.production_unit_id) setUnitId(r.data.production_unit_id);
        loadRmStock((r.data.rm_lines || []).map((l) => l.raw_material_id).filter(Boolean));
      });
    }
  }, [batchId, loadRmStock, setUnitId]);

  const readOnly = !!batchId;
  const rmById = useMemo(() => new Map(rmOptions.map((o) => [o.value, o])), [rmOptions]);

  const updateRmLine = (i, patch) => setForm((f) => {
    const lines = f.rm_lines.map((l, idx) => idx === i ? { ...l, ...patch } : l);
    // Auto-append empty line when the last one has both an RM and a qty
    const last = lines[lines.length - 1];
    if (last && last.raw_material_id && Number(last.issued_qty) > 0) {
      lines.push({ raw_material_id: null, raw_material_name: "", unit: "", issued_qty: "" });
    }
    return { ...f, rm_lines: lines };
  });
  const removeRmLine = (i) => setForm((f) => ({ ...f, rm_lines: f.rm_lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.worker_id) return toast.error("Select a worker");
    if (unitRequired && !unitId) return toast.error("Select a Production Unit");
    const validLines = form.rm_lines.filter((l) => l.raw_material_id && Number(l.issued_qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one RM with qty");
    try {
      setSaving(true);
      await api.post("/production/platings", {
        production_date: form.production_date,
        rm_lines: validLines.map((l) => ({ raw_material_id: l.raw_material_id, issued_qty: Number(l.issued_qty) })),
        worker_id: form.worker_id,
        notes: form.notes || null,
        production_unit_id: unitId || null,
      });
      toast.success("L2 batch issued — RM stock debited");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">{isNew ? "New L2 Plating Batch" : `Batch ${batch?.batch_no || ""}`}</h2>
        {batch?.status && <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[batch.status] || "bg-gray-100 border-gray-200 text-gray-700"}`}>{batch.status.replace("_", " ")}</span>}
        {!readOnly && <div className="ml-auto">
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving} data-testid="plt-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Issue Batch"}
          </Button>
        </div>}
      </div>

      <Surface>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-40">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Production Date</label>
            <Input type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} disabled={readOnly} data-testid="plt-date" />
          </div>
          {unitRequired && (
            <div className="w-64">
              <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Production Unit *</label>
              <ComboBoxSearch value={unitId} onChange={setUnitId} options={unitOptions} placeholder="Select production unit" testId="plt-unit" disabled={readOnly} />
            </div>
          )}
          <div className="w-64">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Worker *</label>
            <ComboBoxSearch value={form.worker_id} onChange={(v) => setForm({ ...form, worker_id: v })} options={workerOptions} placeholder="Select worker" testId="plt-worker" disabled={readOnly} />
          </div>
          <div className="w-80">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" disabled={readOnly} data-testid="plt-notes" />
          </div>
        </div>
      </Surface>

      <Surface padded={false}>
        <div className="p-3 border-b border-gray-200 flex items-center">
          <h3 className="font-heading font-semibold text-sm">RMs to Issue for Plating</h3>
          <span className="ml-auto text-[11px] text-gray-500">A new empty line is added automatically once you enter a qty.</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
              <th className="px-3 py-2 w-10">#</th>
              <th className="px-3 py-2">RM</th>
              <th className="px-3 py-2 text-center w-20">Unit</th>
              <th className="px-3 py-2 text-right w-32">Available</th>
              <th className="px-3 py-2 text-right w-32">Issue Qty</th>
              {readOnly && <th className="px-3 py-2 text-right w-24">Received</th>}
              {readOnly && <th className="px-3 py-2 text-right w-24">Damaged</th>}
              {readOnly && <th className="px-3 py-2 text-right w-24">Lost</th>}
              {!readOnly && <th className="px-3 py-2 w-10"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {form.rm_lines.map((ln, i) => {
              const available = ln.raw_material_id ? rmStockMap[ln.raw_material_id] : undefined;
              const issue = Number(ln.issued_qty) || 0;
              const overIssue = available !== undefined && issue > available;
              return (
                <tr key={i}>
                  <td className="px-3 py-2 text-xs text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2">
                    {readOnly ? <div className="font-medium">{ln.raw_material_name}</div> : (
                      <ComboBoxSearch
                        value={ln.raw_material_id}
                        onChange={(v) => {
                          const opt = rmOptions.find((o) => o.value === v);
                          updateRmLine(i, { raw_material_id: v, raw_material_name: opt?.label, unit: opt?.sublabel || "" });
                          loadRmStock([v]);
                        }}
                        options={ln.raw_material_id && !rmOptions.find((o) => o.value === ln.raw_material_id)
                          ? [...rmOptions, { value: ln.raw_material_id, label: ln.raw_material_name, sublabel: ln.unit || "" }]
                          : rmOptions}
                        onSearchChange={searchRMs}
                        placeholder="Search RM..."
                        testId={`plt-line-${i}-rm`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-gray-600">{ln.unit || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-700">{available === undefined ? "—" : available}</td>
                  <td className="px-3 py-2">
                    {readOnly ? <div className="text-right tabular-nums font-semibold">{ln.issued_qty}</div> : (
                      <Input type="number" step="0.001" value={ln.issued_qty} onChange={(e) => updateRmLine(i, { issued_qty: e.target.value })} className={`text-right tabular-nums ${overIssue ? "border-red-500 text-red-600" : ""}`} data-testid={`plt-line-${i}-qty`} />
                    )}
                    {overIssue && <div className="text-[10px] text-red-600 mt-0.5">Exceeds RM stock</div>}
                  </td>
                  {readOnly && <td className="px-3 py-2 text-right tabular-nums text-xs text-emerald-700 font-semibold">{ln.received_qty || 0}</td>}
                  {readOnly && <td className="px-3 py-2 text-right tabular-nums text-xs text-amber-700 font-semibold">{ln.damaged_qty || 0}</td>}
                  {readOnly && <td className="px-3 py-2 text-right tabular-nums text-xs text-red-700 font-semibold">{ln.lost_qty || 0}</td>}
                  {!readOnly && (
                    <td className="px-3 py-2 text-right">
                      {form.rm_lines.length > 1 && (
                        <button onClick={() => removeRmLine(i)} className="h-6 w-6 rounded hover:bg-red-50 inline-flex items-center justify-center"><X className="h-3 w-3 text-red-500" /></button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Surface>

      {batch && (batch.receipts_history || []).length > 0 && (
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-200 flex items-center">
            <h3 className="font-heading font-semibold text-sm">Receipt History</h3>
            <span className="ml-auto text-xs text-gray-500">
              Received <span className="font-semibold text-emerald-700 tabular-nums">{batch.received_qty || 0}</span> ·
              Damaged <span className="font-semibold text-amber-700 tabular-nums">{batch.damaged_qty || 0}</span> ·
              Lost <span className="font-semibold text-red-700 tabular-nums">{batch.lost_qty || 0}</span>
              {Number(batch.plating_cost || 0) > 0 && <> · Plating Cost <span className="font-semibold text-blue-800">{formatINR(batch.plating_cost || 0)}</span></>}
            </span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2 w-40">Date / Time</th>
                <th className="px-3 py-2">Received By</th>
                <th className="px-3 py-2">RM</th>
                <th className="px-3 py-2 text-right w-20 text-emerald-700">Rcv</th>
                <th className="px-3 py-2 text-right w-20 text-amber-700">Dmg</th>
                <th className="px-3 py-2 text-right w-20 text-red-700">Lost</th>
                <th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(batch.receipts_history || []).flatMap((h) => (
                (h.lines || []).length
                  ? (h.lines || []).map((l, idx) => (
                      <tr key={`${h.id}-${idx}`} className="hover:bg-gray-50">
                        {idx === 0 && <td className="px-3 py-2 tabular-nums align-top" rowSpan={h.lines.length}>{formatDateTime(h.received_at)}</td>}
                        {idx === 0 && <td className="px-3 py-2 align-top" rowSpan={h.lines.length}>{h.received_by_name || h.received_by_email || "—"}</td>}
                        <td className="px-3 py-2 font-medium">{l.raw_material_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{l.received_qty || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-700">{l.damaged_qty || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">{l.lost_qty || 0}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {l.notes || (idx === 0 ? h.notes : "") || "—"}
                          {idx === 0 && h.closed_here && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold">· closed</span>}
                        </td>
                      </tr>
                    ))
                  : [
                      <tr key={h.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 tabular-nums">{formatDateTime(h.received_at)}</td>
                        <td className="px-3 py-2">{h.received_by_name || h.received_by_email || "—"}</td>
                        <td className="px-3 py-2 text-gray-400 italic" colSpan={4}>Billing-only update</td>
                        <td className="px-3 py-2 text-gray-600">{h.notes || "—"}</td>
                      </tr>,
                    ]
              ))}
            </tbody>
          </table>
        </Surface>
      )}

      {batch && Number(batch.plating_cost || 0) > 0 && (
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
            <h3 className="font-heading font-semibold text-sm">Outsourced Plating Bill</h3>
            <span className="text-xs text-gray-500">
              Total Cost <span className="font-semibold text-blue-800 tabular-nums">{formatINR(batch.plating_cost || 0)}</span>
              {batch.plating_receipt_no && <> · Latest Receipt <span className="font-mono font-semibold text-gray-800">{batch.plating_receipt_no}</span></>}
              {batch.worker_name && <> · Credited to <span className="font-semibold text-gray-800">{batch.worker_name}</span></>}
            </span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2 w-40">Date / Time</th>
                <th className="px-3 py-2">Recorded By</th>
                <th className="px-3 py-2 w-40">Receipt / Bill No</th>
                <th className="px-3 py-2 text-right w-32">Amount</th>
                <th className="px-3 py-2 text-right w-32">Running Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(batch.plating_cost_history || []).map((h) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 tabular-nums">{formatDateTime(h.recorded_at)}</td>
                  <td className="px-3 py-2">{h.recorded_by_name || "—"}</td>
                  <td className="px-3 py-2 font-mono">{h.receipt_no || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-800">{formatINR(h.amount || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{formatINR(h.running_total || 0)}</td>
                </tr>
              ))}
              {(!batch.plating_cost_history || !batch.plating_cost_history.length) && (
                <tr><td colSpan={5} className="px-3 py-3 text-gray-400 italic">Legacy bill — no per-entry history recorded.</td></tr>
              )}
              {batch.plating_cost_history && batch.plating_cost_history.length > 0 && (
                <tr className="bg-blue-50/50 font-semibold border-t-2 border-blue-200">
                  <td className="px-3 py-2" colSpan={3}>Total ({batch.plating_cost_history.length} entr{batch.plating_cost_history.length === 1 ? "y" : "ies"})</td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-900">{formatINR(batch.plating_cost || 0)}</td>
                  <td className="px-3 py-2"></td>
                </tr>
              )}
            </tbody>
          </table>
        </Surface>
      )}
    </div>
  );
}

// ------------------ Receive Dialog ------------------

function PlatingReceiveDialog({ batch, onClose, onDone }) {
  const lines = batch.rm_lines || [];
  const [rowInputs, setRowInputs] = useState({});       // { plt_line_id: {received, damaged, lost, notes} }
  const [notes, setNotes] = useState("");
  // Incremental — each save adds this amount to the running total and posts
  // an earning entry to the plating worker's ledger. Left blank by default.
  const [platingCost, setPlatingCost] = useState("");
  const [platingReceiptNo, setPlatingReceiptNo] = useState("");
  const [saving, setSaving] = useState(false);
  const runningTotal = Number(batch.plating_cost || 0);

  const setField = (id, field, v) => setRowInputs((s) => ({ ...s, [id]: { ...(s[id] || {}), [field]: v } }));

  const submit = async () => {
    const linesPayload = lines
      .map((l) => {
        const row = rowInputs[l.id] || {};
        return {
          plt_line_id: l.id,
          received_qty: Number(row.received) || 0,
          damaged_qty: Number(row.damaged) || 0,
          lost_qty: Number(row.lost) || 0,
          notes: row.notes || null,
        };
      })
      .filter((x) => x.received_qty > 0 || x.damaged_qty > 0 || x.lost_qty > 0);
    const billingHasCost = Number(platingCost) > 0.0001;
    const billingHasReceipt = platingReceiptNo.trim().length > 0;
    if (linesPayload.length === 0 && !billingHasCost && !billingHasReceipt) {
      return toast.error("Enter a received / damaged / lost qty (or a Plating Cost / Receipt No)");
    }
    try {
      setSaving(true);
      const payload = {
        lines: linesPayload,
        receive_notes: notes || null,
      };
      // Only send billing fields when the user actually typed something. Cost
      // is INCREMENTAL — the backend adds it to the running total and ledger.
      if (billingHasCost) payload.plating_cost = Number(platingCost);
      if (billingHasReceipt) payload.plating_receipt_no = platingReceiptNo.trim();
      await api.post(`/production/platings/${batch.id}/receive`, payload);
      toast.success(`Batch ${batch.batch_no} — updated`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Receive failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl" data-testid="plt-receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive Finished — {batch.batch_no}</DialogTitle>
          <DialogDescription>
            For every issued piece, the sum of <b>Received + Damaged + Lost</b> across all receives must equal the issued qty. Received goes into Finished Stocks (Ready for QC); Damaged &amp; Lost are logged for audit. The batch <b>closes automatically</b> once every line is fully accounted for.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-1">
          <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">Overall Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. first half returned, gold plating batch 1" className="max-w-xl" data-testid="plt-receive-notes" />
        </div>
        <div className="border border-gray-200 rounded-md overflow-hidden mt-3">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">RM</th>
                <th className="px-3 py-2 text-right w-16">Issued</th>
                <th className="px-3 py-2 text-right w-20">Received<br/><span className="text-emerald-700 font-normal">so far</span></th>
                <th className="px-3 py-2 text-right w-20">Damaged<br/><span className="text-amber-700 font-normal">so far</span></th>
                <th className="px-3 py-2 text-right w-20">Lost<br/><span className="text-red-700 font-normal">so far</span></th>
                <th className="px-3 py-2 text-right w-20">Pending</th>
                <th className="px-3 py-2 text-right w-24">Receive Now</th>
                <th className="px-3 py-2 text-right w-24">Damaged Now</th>
                <th className="px-3 py-2 text-right w-24">Lost Now</th>
                <th className="px-3 py-2 w-48">Line Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((l) => {
                const alrRcv = Number(l.received_qty || 0);
                const alrDmg = Number(l.damaged_qty || 0);
                const alrLost = Number(l.lost_qty || 0);
                const issued = Number(l.issued_qty || 0);
                const done = alrRcv + alrDmg + alrLost;
                const pending = Math.max(0, issued - done);
                const row = rowInputs[l.id] || {};
                const requested = (Number(row.received) || 0) + (Number(row.damaged) || 0) + (Number(row.lost) || 0);
                const over = requested > pending + 0.001;
                const isDone = pending <= 0.001;
                return (
                  <tr key={l.id}>
                    <td className="px-3 py-2 font-medium">
                      {l.raw_material_name} <span className="text-[10px] text-gray-400 font-normal">{l.unit}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{issued}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-emerald-700">{alrRcv || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-amber-700">{alrDmg || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-red-700">{alrLost || "—"}</td>
                    <td className={`px-3 py-2 text-right tabular-nums text-xs font-bold ${isDone ? "text-gray-400" : "text-[#9B1C1C]"}`}>{isDone ? "Done" : pending}</td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.001" min="0" value={row.received || ""}
                        onChange={(e) => setField(l.id, "received", e.target.value)}
                        disabled={isDone}
                        className={`text-right tabular-nums h-8 w-full ${over ? "border-red-500" : ""}`}
                        data-testid={`plt-rcv-${l.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.001" min="0" value={row.damaged || ""}
                        onChange={(e) => setField(l.id, "damaged", e.target.value)}
                        disabled={isDone}
                        className={`text-right tabular-nums h-8 w-full ${over ? "border-red-500" : ""}`}
                        data-testid={`plt-dmg-${l.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.001" min="0" value={row.lost || ""}
                        onChange={(e) => setField(l.id, "lost", e.target.value)}
                        disabled={isDone}
                        className={`text-right tabular-nums h-8 w-full ${over ? "border-red-500" : ""}`}
                        data-testid={`plt-lost-${l.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Input value={row.notes || ""}
                        onChange={(e) => setField(l.id, "notes", e.target.value)}
                        placeholder="Optional"
                        className="h-8 text-xs w-full" data-testid={`plt-note-${l.id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Optional plating billing — used when the plating work is outsourced. Editable while the batch is open. */}
        <div className="mt-4 rounded-md border border-blue-100 bg-blue-50/40 px-4 py-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-widest text-blue-900 font-semibold">Plating Bill (optional — for outsourced work)</div>
            {runningTotal > 0 && (
              <div className="text-xs text-blue-900">
                Running Total: <span className="font-semibold tabular-nums">{`₹${runningTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-lg">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Plating Cost — this receipt</label>
              <Input type="number" step="0.01" min="0" value={platingCost}
                onChange={(e) => setPlatingCost(e.target.value)}
                placeholder="0.00"
                className="h-8 text-right tabular-nums w-40"
                data-testid="plt-billing-cost" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Receipt / Bill Number</label>
              <Input value={platingReceiptNo}
                onChange={(e) => setPlatingReceiptNo(e.target.value)}
                placeholder="e.g. INV-2451"
                className="h-8 w-48"
                data-testid="plt-billing-receipt" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-2">
            The amount is <b>added</b> to the running total on each save and credited to <b>{batch.worker_name || "the plating worker"}</b>&apos;s ledger. Leave blank if this receive has no outsourced cost — nothing will be posted.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5 text-white" onClick={submit} disabled={saving} data-testid="plt-receive-submit">
            <CheckCircle2 className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, className = "", children }) {
  return <label className={`text-sm ${className}`}><span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>{children}</label>;
}
